<script setup lang="ts">
import { computed } from 'vue'
import type { Table, Action, TableColumnDefinition } from '../types'
import { cn } from '@/lib/utils'
import { spacingClasses } from '../utils/classes'
import ACElement from './ACElement.vue'

const props = defineProps<{
  element: Table
  onAction?: (action: Action, inputs: Record<string, string>) => void
}>()

const gridStyleClasses: Record<string, string> = {
  default: '',
  emphasis: 'bg-muted',
  good: 'bg-green-50 dark:bg-green-950',
  attention: 'bg-yellow-50 dark:bg-yellow-950',
  warning: 'bg-orange-50 dark:bg-orange-950',
  accent: 'bg-blue-50 dark:bg-blue-950',
}

const alignClasses: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

const verticalAlignClasses: Record<string, string> = {
  top: 'align-top',
  center: 'align-middle',
  bottom: 'align-bottom',
}

function getColumnWidth(col: TableColumnDefinition): string {
  if (!col.width) return 'auto'
  if (typeof col.width === 'number') return `minmax(0, ${col.width}fr)`
  if (col.width === 'auto') return 'auto'
  if (col.width.endsWith('px')) return col.width
  // Treat as weight for grid
  return `minmax(0, ${col.width}fr)`
}

const gridTemplateColumns = computed(() => {
  return props.element.columns
    .map((col) => getColumnWidth(col))
    .join(' ')
})

function getCellAlignment(colIndex: number): string {
  const col = props.element.columns[colIndex]
  const hAlign = col?.horizontalCellContentAlignment ?? props.element.horizontalCellContentAlignment ?? 'left'
  return alignClasses[hAlign] ?? ''
}

function getCellVerticalAlignment(colIndex: number): string {
  const col = props.element.columns[colIndex]
  const vAlign = col?.verticalCellContentAlignment ?? props.element.verticalCellContentAlignment ?? 'top'
  return verticalAlignClasses[vAlign] ?? ''
}
</script>

<template>
  <div
    :class="cn(
      'w-full overflow-auto',
      spacingClasses[props.element.spacing ?? 'default'],
      gridStyleClasses[props.element.gridStyle ?? 'default']
    )"
  >
    <div
      class="grid min-w-full"
      :style="{ gridTemplateColumns }"
      :class="cn(props.element.showGridLines !== false && 'border border-border rounded-md')"
    >
      <template v-for="(row, rowIndex) in props.element.rows" :key="rowIndex">
        <template v-for="(cell, cellIndex) in row.cells" :key="`${rowIndex}-${cellIndex}`">
          <div
            :class="cn(
              'p-2',
              getCellAlignment(cellIndex),
              getCellVerticalAlignment(cellIndex),
              props.element.showGridLines !== false && 'border-b border-r border-border last:border-r-0',
              props.element.firstRowAsHeader && rowIndex === 0 && 'font-semibold bg-muted/50',
              gridStyleClasses[row.style ?? 'default'],
              gridStyleClasses[cell.style ?? 'default']
            )"
          >
            <ACElement
              v-for="(item, itemIndex) in cell.items"
              :key="itemIndex"
              :element="item"
              :on-action="props.onAction"
            />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
